import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import * as Dialog from '@radix-ui/react-dialog';
import { Toast } from '../components/ui/Toast';
import { LongToast, shouldUseLongToast } from '../components/ui/LongToast';
import { Search, Filter, UserMinus, UserX, Download, FileText, Settings, ArrowUp, ArrowDown, ArrowLeft, RefreshCw, Edit2, User } from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import DetailModal from '../components/EmployeeDetailModal';
import { parseISO, format } from 'date-fns';
import RadiationEmployeDialog from './employes/RadiationEmployeDialog';
import EmployesRadies from './employes/EmployesRadies';
import Pagination from './ui/Pagination';
import ChampSelect from './ui/ChampSelect';
import { usePermissionsRadiationEmployes } from '../hooks/useRadiationEmployes';
import StatistiquesRoles from './StatistiquesRoles';
import { aUnePhoto, PARAM_PHOTO, VALEUR_SANS_PHOTO } from '../utils/photos';

// ── LIEN DIRECT « SANS PHOTO » ───────────────────────────────────────────────
// `/administration/ressources-humaines?tab=liste&photo=sans` ouvre la liste
// filtrée sur le personnel sans photo ; `&categorie=ENSEIGNEMENT` ne garde que
// les enseignants (la tuile « Enseignants » de la répartition par catégorie).
// Le panneau des alertes s'en servira.
//
// Le lien repart d'une liste propre (les autres filtres sont remis à zéro) :
// le chiffre annoncé par le panneau doit égaler la liste qu'il ouvre. Il est lu
// au montage ET à chaque nouvelle navigation, parce que le panneau fait
// navigate() dans la même application, l'écran déjà ouvert. Puis `photo` et
// `categorie` sont retirés de l'adresse (remplacement, sans entrée
// d'historique) : un F5 rend la liste complète. `tab` reste, comme ailleurs.
const PARAM_CATEGORIE = 'categorie';
const CATEGORIES_RH = ['ENSEIGNEMENT', 'ADMINISTRATION', 'DIRECTION', 'SUPPORT_TECHNIQUE', 'AUTRE'];

const lireLienFiltres = (search) => {
  const parametres = new URLSearchParams(search || '');
  if (!parametres.has(PARAM_PHOTO) && !parametres.has(PARAM_CATEGORIE)) return null;
  const categorie = parametres.get(PARAM_CATEGORIE);
  return {
    photo: parametres.get(PARAM_PHOTO) === VALEUR_SANS_PHOTO ? VALEUR_SANS_PHOTO : '',
    categorie: CATEGORIES_RH.includes(categorie) ? categorie : ''
  };
};


const EmployeesList = (props) => {
  const navigate = useNavigate();
  const location = useLocation();

  // États principaux
  const [currentUser, setCurrentUser] = useState(null);
  const { peutRadier, peutVoirRadies } = usePermissionsRadiationEmployes();
  // Les employés radiés s'affichent à la place de la liste, sans changer
  // d'onglet (demande de Daniel, 18 septembre 2026).
  const [vueRadies, setVueRadies] = useState(false);
  const [radiesModifies, setRadiesModifies] = useState(false);
  const [activeYear, setActiveYear] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'nom', direction: 'asc' });
  const [filters, setFilters] = useState({
    departement: '',
    role: '',
    statut: '',
    dateDebut: '',
    dateFin: '',
  });
  // « Sans photo » : '' (tout le personnel) ou 'sans'. Se combine avec les
  // tuiles de catégorie : « Enseignants » + « Sans photo » = les enseignants
  // sans photo, sans écran de plus.
  const [filtrePhoto, setFiltrePhoto] = useState('');

  const [showFilters, setShowFilters] = useState(false);
  const [departements, setDepartements] = useState([]);
  const [roles, setRoles] = useState([]);


  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // États modaux et édition
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedEmployee, setEditedEmployee] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // États suppression/désactivation
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);

  // États toast
  const [toastInfo, setToastInfoState] = useState(null);

  // Etats pour les stats
  const [statsRoles, setStatsRoles] = useState([]);
  const [statsCategories, setStatsCategories] = useState([]);
  const [filterRole, setFilterRole] = useState('');
  const [filterCategorie, setFilterCategorie] = useState('');

  // Fonctions utilitaires
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy');
    } catch (error) {
      return dateString;
    }
  };

  const getDateInputValue = (dateValue) => {
    if (!dateValue) return '';

    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}/.test(dateValue)) {
      return dateValue.slice(0, 10);
    }

    try {
      return format(new Date(dateValue), 'yyyy-MM-dd');
    } catch (error) {
      return '';
    }
  };

  const getEmployeeEntryDateValue = (employee = {}) => (
    getDateInputValue(employee.date_embauche || employee.date_entree)
  );

  const updateFilters = (nextFilters) => {
    setFilters(prev => ({ ...prev, ...nextFilters }));
    setCurrentPage(1);
  };

  const setRecentDateFilter = (days) => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - (days - 1));

    updateFilters({
      dateDebut: format(startDate, 'yyyy-MM-dd'),
      dateFin: format(endDate, 'yyyy-MM-dd')
    });
  };

  const setCurrentYearDateFilter = () => {
    updateFilters({
      dateDebut: getDateInputValue(activeYear?.date_debut) || '',
      dateFin: getDateInputValue(activeYear?.date_fin) || ''
    });
  };

  const resetListFilters = () => {
    setSearchTerm('');
    setFilterRole('');
    setFilterCategorie('');
    setFiltrePhoto('');
    setFilters({
      departement: '',
      role: '',
      statut: '',
      dateDebut: '',
      dateFin: ''
    });
    setCurrentPage(1);
  };

  // Les filtres autres que « Sans photo » : ils décident du message d'une liste
  // « Sans photo » vide (tout le personnel, ou seulement le personnel filtré).
  const autresFiltresActifs = Boolean(
    searchTerm ||
    filterRole ||
    filterCategorie ||
    filters.departement ||
    filters.role ||
    filters.statut ||
    filters.dateDebut ||
    filters.dateFin
  );

  const hasActiveFilters = autresFiltresActifs || filtrePhoto !== '';

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const normalizeEmployeeRoles = (employee = {}) => {
    const rolesDetails = Array.isArray(employee.roles_details) ? employee.roles_details : [];
    if (rolesDetails.length > 0) return rolesDetails;

    const fallbackRoleName = employee.poste_metier_nom || employee.role_nom;
    const fallbackGroupName = employee.groupe_nom || employee.role_nom;
    if (!fallbackRoleName && !fallbackGroupName) return [];

    return [{
      role_nom: fallbackRoleName || 'Non défini',
      groupe_nom: fallbackGroupName || 'Non défini',
      est_role_principal: true,
      mode_remuneration: employee.mode_remuneration || employee.type_contrat || employee.info_pro_type_contrat,
      categorie_rh: employee.categorie_rh || 'AUTRE',
      categorie_rh_libelle: employee.categorie_rh_libelle || 'Autres'
    }];
  };

  const getPrimaryRole = (employee = {}) => {
    const rolesDetails = normalizeEmployeeRoles(employee);
    return rolesDetails.find(role => role.est_role_principal) || rolesDetails[0] || null;
  };

  const getSecondaryRoles = (employee = {}) => normalizeEmployeeRoles(employee)
    .filter(role => !role.est_role_principal);

  const getPosteMetier = (employee) => getPrimaryRole(employee)?.role_nom || employee.poste_metier_nom || employee.role_nom || 'Non défini';
  const getGroupeSecurite = (employee) => getPrimaryRole(employee)?.groupe_nom || employee.groupe_nom || employee.role_nom || 'Non défini';
  const getCategorieRh = (employee) => getPrimaryRole(employee)?.categorie_rh_libelle || employee.categorie_rh_libelle || 'Autres';

  const getAllPostesMetiers = (employee) => [...new Set(normalizeEmployeeRoles(employee)
    .map(role => role.role_nom)
    .filter(Boolean))].join(', ') || getPosteMetier(employee);

  const getAllGroupesSecurite = (employee) => [...new Set(normalizeEmployeeRoles(employee)
    .map(role => role.groupe_nom)
    .filter(Boolean))].join(', ') || getGroupeSecurite(employee);

  const getAllCategoriesRh = (employee) => [...new Map(normalizeEmployeeRoles(employee)
    .map(role => [role.categorie_rh || 'AUTRE', role.categorie_rh_libelle || 'Autres']))
    .entries()].map(([key, label]) => ({ key, label }));

  const getPostesMetiersDetails = (employee = {}) => {
    const roles = normalizeEmployeeRoles(employee).filter(role => role.role_nom);
    const primaryRole = roles.find(role => role.est_role_principal) || roles[0];
    const secondaryRoles = roles.filter(role => role !== primaryRole);
    return primaryRole ? [primaryRole, ...secondaryRoles] : [];
  };

  const shouldUseCompactPostes = (employee = {}) => (
    employee.profil_remuneration === 'HYBRIDE' || getPostesMetiersDetails(employee).length > 1
  );

  const renderCompactPosteBadge = (role, index) => (
    <span
      key={role.employe_role_id || role.role_id || role.role_nom || index}
      className={`inline-flex h-[34px] w-[136px] shrink-0 items-center justify-center overflow-hidden rounded-full px-3 text-center text-[12px] font-medium leading-[14px] ${index === 0 ? 'bg-[#246990]/10 text-[#246990] border border-[#246990]/15' : 'bg-[#319190]/10 text-[#319190] border border-[#319190]/20'}`}
      title={role.role_nom || 'Non défini'}
    >
      <span className="max-w-full whitespace-normal break-words">
        {role.role_nom || 'Non défini'}
      </span>
    </span>
  );

  const getProfilRemunerationLabel = (employee = {}) => {
    const profil = employee.profil_remuneration || '';
    if (profil === 'HYBRIDE') return 'Hybride';
    if (profil === 'HORAIRE') return 'Horaire';
    if (profil === 'MENSUEL') return 'Mensuel';
    return 'Non défini';
  };

  const getProfilBadgeClass = (employee = {}) => {
    const profil = employee.profil_remuneration || '';
    if (profil === 'HYBRIDE') return 'bg-[#c66d59]/15 text-[#c66d59] border-[#c66d59]/30';
    if (profil === 'HORAIRE') return 'bg-[#319190]/15 text-[#319190] border-[#319190]/30';
    if (profil === 'MENSUEL') return 'bg-[#246990]/10 text-[#246990] border-[#246990]/20';
    return 'bg-gray-100 text-gray-600 border-gray-200';
  };

  const employeeMatchesRole = (employee, roleName) => !roleName || normalizeEmployeeRoles(employee)
    .some(role => role.role_nom === roleName);

  const employeeMatchesCategory = (employee, categoryKey) => !categoryKey || normalizeEmployeeRoles(employee)
    .some(role => role.categorie_rh === categoryKey);

  // Logique de filtrage : d'abord tous les filtres sauf « Sans photo ».
  const employeesAutresFiltres = Array.isArray(employees)
    ? employees.filter(employee => {
      const search = searchTerm.toLowerCase();
      const roleSearchText = [
        getAllPostesMetiers(employee),
        getAllGroupesSecurite(employee),
        getAllCategoriesRh(employee).map(category => category.label).join(' '),
        getProfilRemunerationLabel(employee)
      ].join(' ').toLowerCase();
      const employeeEntryDate = getEmployeeEntryDateValue(employee);

      const matchesSearch =
        (employee.nom?.toLowerCase() || '').includes(search) ||
        (employee.prenom?.toLowerCase() || '').includes(search) ||
        (employee.email?.toLowerCase() || '').includes(search) ||
        (employee.telephone?.toLowerCase() || '').includes(search) ||
        roleSearchText.includes(search);

      const matchesRole = employeeMatchesRole(employee, filterRole);
      const matchesCategorie = employeeMatchesCategory(employee, filterCategorie);
      const matchesStatut = !filters.statut || employee.statut === filters.statut;
      const matchesDateDebut = !filters.dateDebut || (employeeEntryDate && employeeEntryDate >= filters.dateDebut);
      const matchesDateFin = !filters.dateFin || (employeeEntryDate && employeeEntryDate <= filters.dateFin);
      const matchesLegacyRoleFilter = !filters.role || normalizeEmployeeRoles(employee).some(role =>
        role.role_nom === filters.role || role.groupe_nom === filters.role
      );

      return matchesSearch && matchesRole && matchesCategorie && matchesStatut && matchesDateDebut && matchesDateFin && matchesLegacyRoleFilter;
    })
    : [];

  // Puis « Sans photo ». Le chiffre de la bascule se compte sur la même liste
  // que celle qu'elle rend : il égale toujours le nombre de lignes affichées une
  // fois la bascule activée, quels que soient les autres filtres. Aucune
  // exclusion par le nom (kiosque, comptes de test) : la règle est aUnePhoto,
  // la même que le serveur et le panneau des alertes.
  const employeesSansPhoto = employeesAutresFiltres.filter(employee => !aUnePhoto(employee));
  const filtreSansPhotoActif = filtrePhoto === VALEUR_SANS_PHOTO;
  const filteredEmployees = filtreSansPhotoActif ? employeesSansPhoto : employeesAutresFiltres;

  const getSortValue = (employee, key) => {
    if (key === 'categorie_rh_libelle') return getCategorieRh(employee);
    if (key === 'poste_metier_nom') return getPosteMetier(employee);
    if (key === 'groupe_nom') return getGroupeSecurite(employee);
    if (key === 'profil_remuneration') return getProfilRemunerationLabel(employee);
    if (key === 'date_embauche') return getEmployeeEntryDateValue(employee);
    return employee?.[key] ?? '';
  };

  // Trier les employés
  const sortedEmployees = [...filteredEmployees].sort((a, b) => {
    const aValue = getSortValue(a, sortConfig.key);
    const bValue = getSortValue(b, sortConfig.key);

    if (!aValue && !bValue) return 0;
    if (!aValue) return 1;
    if (!bValue) return -1;

    const comparison = String(aValue).localeCompare(String(bValue), 'fr', {
      sensitivity: 'base',
      numeric: true
    });

    return sortConfig.direction === 'asc' ? comparison : -comparison;
  });

  // Calcul des éléments pour la page courante. Une page vidée sous les yeux
  // (photo posée sous « Sans photo », radiation) ramène à la dernière page qui
  // existe : sinon le tableau restait vide, sans barre de pagination pour en
  // sortir (elle se masque quand tout tient sur une page).
  const pageCourante = Math.min(currentPage, Math.max(1, Math.ceil(sortedEmployees.length / itemsPerPage)));
  const indexOfLastItem = pageCourante * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedEmployees = sortedEmployees.slice(indexOfFirstItem, indexOfLastItem);


  useEffect(() => {
    setTotalPages(Math.ceil(sortedEmployees.length / itemsPerPage));
  }, [sortedEmployees, itemsPerPage]);


  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        // Récupérer l'année active
        const activeYearResponse = await api.anneesScolaires.getActive();

        // Récupérer l'utilisateur connecté depuis localStorage
        const userData = JSON.parse(localStorage.getItem('user') || '{}');
        setCurrentUser(userData);

        if (activeYearResponse.success && activeYearResponse.data) {
          setActiveYear(activeYearResponse.data);

          // Charger les employés et les données de référence
          try {
            const employeesResponse = await api.employes.getAll();
            // S'assurer que employeesResponse est un tableau
            const employeesData = Array.isArray(employeesResponse)
              ? employeesResponse
              : (employeesResponse?.data || []);

            console.log('Données employés reçues:', employeesData);
            setEmployees(employeesData);

            const departementsData = await api.departements.getAll();
            const rolesData = await api.roles.getAll();

            setDepartements(Array.isArray(departementsData) ? departementsData : (departementsData?.data || []));
            setRoles(Array.isArray(rolesData) ? rolesData : (rolesData?.data || []));
          } catch (fetchError) {
            console.error('Erreur lors de la récupération des données:', fetchError);
            const message = `Erreur de chargement: ${fetchError.message}`;
            setError(message);
            showErrorToast(message);
          }
        } else {
          const message = "Erreur lors du chargement des données d'année scolaire";
          setError(message);
          showErrorToast(message);
        }
      } catch (error) {
        console.error('Erreur globale:', error);
        const message = `Erreur lors du chargement des données: ${error.message}`;
        setError(message);
        showErrorToast(message);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);


  // Calculer les statistiques par categorie RH et poste metier
  useEffect(() => {
    if (employees.length > 0) {
      const categoryOrder = CATEGORIES_RH;
      const getRolesForStats = (employee) => {
        const rolesDetails = Array.isArray(employee.roles_details) ? employee.roles_details : [];
        if (rolesDetails.length > 0) return rolesDetails;
        return [{
          role_nom: employee.poste_metier_nom || employee.role_nom || 'Non défini',
          categorie_rh: employee.categorie_rh || 'AUTRE',
          categorie_rh_libelle: employee.categorie_rh_libelle || 'Autres'
        }];
      };

      const categoryStats = employees.reduce((acc, employee) => {
        const categories = [...new Map(getRolesForStats(employee)
          .map(role => [role.categorie_rh || 'AUTRE', role.categorie_rh_libelle || 'Autres']))
          .entries()].map(([key, label]) => ({ key, label }));
        const normalizedCategories = categories.length > 0 ? categories : [{ key: 'AUTRE', label: 'Autres' }];

        normalizedCategories.forEach(category => {
          if (!acc[category.key]) {
            acc[category.key] = { role_nom: category.label, filter_value: category.key, employeeIds: new Set() };
          }
          acc[category.key].employeeIds.add(employee.id);
        });

        return acc;
      }, {});

      const roleStats = employees.reduce((acc, employee) => {
        const roleNames = [...new Set(getRolesForStats(employee)
          .map(role => role.role_nom || 'Non défini'))];

        roleNames.forEach(roleName => {
          if (!acc[roleName]) {
            acc[roleName] = new Set();
          }
          acc[roleName].add(employee.id);
        });

        return acc;
      }, {});

      const categoryArray = Object.values(categoryStats)
        .map(stat => ({
          role_nom: stat.role_nom,
          filter_value: stat.filter_value,
          nombre_employes: stat.employeeIds.size
        }))
        .sort((a, b) => categoryOrder.indexOf(a.filter_value) - categoryOrder.indexOf(b.filter_value));

      const statsArray = Object.entries(roleStats).map(([role_nom, employeeIds]) => ({
        role_nom,
        nombre_employes: employeeIds.size
      })).sort((a, b) => b.nombre_employes - a.nombre_employes);

      setStatsCategories(categoryArray);
      setStatsRoles(statsArray);
    } else {
      setStatsCategories([]);
      setStatsRoles([]);
    }
  }, [employees]);




  // ⚠️ `duration` FAIT PARTIE DE LA LISTE, et son absence était un piège.
  // Cette fonction déstructure explicitement les options qu'elle accepte : toute
  // option absente de cette liste est jetée EN SILENCE. Un `{ duration: 8000 }`
  // posé par un appelant disparaissait donc sans trace, et le message repartait
  // pour 3 secondes — le correctif aurait eu l'air posé sans rien faire.
  const setToastInfo = ({ message, type = 'success', title, long, persistent, duration } = {}) => {
    if (!message) return;
    setToastInfoState({ message, type, title, long, persistent, duration });
  };

  const showSuccessToast = (message, options = {}) => {
    setToastInfo({ ...options, message, type: 'success' });
  };

  const showErrorToast = (message, options = {}) => {
    setToastInfo({ ...options, message, type: 'error' });
  };

  const handleEditStart = () => {
    setIsEditing(true);
    // Sauvegarder l'état initial pour pouvoir revenir en arrière si annulation
    setEditedEmployee(selectedEmployee);
  };

  // fonctions pour gérer les exports 
  const handleExportExcel = () => {
    try {
      // Préparer les données pour l'export
      const dataToExport = sortedEmployees.map(employee => ({
        'Nom': employee.nom,
        'Prénom': employee.prenom,
        'Email': employee.email,
        'Téléphone': employee.telephone,
        'Catégorie RH': getCategorieRh(employee),
        'Poste métier principal': getPosteMetier(employee),
        'Tous les postes métier': getAllPostesMetiers(employee),
        'Groupe sécurité principal': getGroupeSecurite(employee),
        'Tous les groupes sécurité': getAllGroupesSecurite(employee),
        'Profil rémunération': getProfilRemunerationLabel(employee),
        'Nombre de rôles': employee.nombre_roles || normalizeEmployeeRoles(employee).length,
        'Département': employee.departement_nom,
        'Date d\'embauche': employee.date_embauche ? formatDate(employee.date_embauche) : '',
        'Statut': employee.statut || 'Actif',
        'Adresse': employee.adresse
      }));

      // Créer un nouveau classeur
      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Liste des employés");

      // Générer le fichier
      XLSX.writeFile(wb, `Liste_des_employes_${new Date().toLocaleDateString()}.xlsx`);
      showSuccessToast('Export Excel réussi');
    } catch (error) {
      showErrorToast("Erreur lors de l'export Excel");
    }
  };


  const handleExportPDF = async () => {
    try {
      console.log('🔍 Début export PDF');
      setToastInfo({ message: 'Génération du PDF en cours...', type: 'loading' });

      // ✅ Construire les paramètres de filtrage
      const params = new URLSearchParams();
      if (filterRole) params.append('poste_metier', filterRole);
      if (filterCategorie) params.append('categorie_rh', filterCategorie);
      if (filters.departement) params.append('departement', filters.departement);
      if (filters.statut) params.append('statut', filters.statut);
      if (filters.dateDebut) params.append('date_debut', filters.dateDebut);
      if (filters.dateFin) params.append('date_fin', filters.dateFin);

      console.log('📤 Paramètres:', params.toString());

      // ✅ Utiliser la fonction de l'API (gère automatiquement l'URL et les headers)
      const response = await api.employes.exportPDF(params);

      console.log('📥 Status reçu:', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Erreur serveur:', errorText);
        throw new Error(`Erreur HTTP ${response.status}: ${errorText}`);
      }

      const blob = await response.blob();
      console.log('✅ Blob reçu, taille:', blob.size, 'bytes');

      // Télécharger le fichier
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `Liste_Employes_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);

      showSuccessToast('PDF généré avec succès');

    } catch (error) {
      console.error('❌ Erreur export PDF:', error);
      showErrorToast(`Erreur: ${error.message}`);
    }
  };




  const handleEditSubmit = async (updatedEmployee) => {
    try {
      console.log('Données à mettre à jour:', updatedEmployee);

      // Appeler la méthode update de l'API
      const response = await api.employes.update(updatedEmployee.id, updatedEmployee);

      if (response.success) {
        // Mise à jour des données locales
        setEmployees(prev => prev.map(employee =>
          employee.id === updatedEmployee.id ?
            { ...employee, ...updatedEmployee } : employee
        ));

        // Mettre à jour l'employé sélectionné si nécessaire
        if (selectedEmployee && selectedEmployee.id === updatedEmployee.id) {
          setSelectedEmployee(prev => ({ ...prev, ...updatedEmployee }));
        }

        setIsEditing(false);
        showSuccessToast('Les modifications ont été enregistrées avec succès');

        // Propager les modifications au composant parent HRManagement
        if (props.onEmployeeUpdate) {
          props.onEmployeeUpdate(updatedEmployee);
        }

        // Actualiser la liste si nécessaire
        refreshEmployeesList();
      } else {
        throw new Error(response.message || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour:', error);
      // ⚠️ L'ERREUR REMONTE À LA FICHE, QUI L'AFFICHE ET RESTE EN MODIFICATION.
      // L'avaler ici faisait mentir la fiche : elle annonçait « Informations
      // mises à jour avec succès », et son toast écrasait aussitôt celui de
      // l'erreur — les deux passent par `setToastInfo`. Un badge scanné
      // paraissait enregistré, puis avait disparu à la réouverture (03/11/2026).
      // Pas de toast ici : c'est la fiche qui le pose, avec le motif du serveur.
      throw error;
    }
  };


  // Fonction pour rafraîchir la liste complète des employés


  const refreshEmployeesList = async () => {
    try {
      setLoading(true);
      const employeesResponse = await api.employes.getAll();
      const employeesData = Array.isArray(employeesResponse)
        ? employeesResponse
        : (employeesResponse?.data || []);

      setEmployees(employeesData);

      // Informer le composant parent de l'actualisation
      if (props.onEmployeesListRefresh) {
        props.onEmployeesListRefresh(employeesData);
      }

      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des données:', error);
      const message = `Erreur de chargement: ${error.message}`;
      setError(message);
      showErrorToast(message);
      setLoading(false);
    }
  };

  const handleConfirmEdit = async () => {
    try {
      // S'assurer que les données sont structurées correctement
      const updateData = {
        nom: editedEmployee.nom,
        prenom: editedEmployee.prenom,
        email: editedEmployee.email,
        telephone: editedEmployee.telephone,
        adresse: editedEmployee.adresse,
        role_id: editedEmployee.role_id,
        departement_id: editedEmployee.departement_id,
        date_embauche: editedEmployee.date_embauche,
        statut: editedEmployee.statut
      };

      console.log('Données envoyées pour mise à jour:', updateData);

      // Faire la mise à jour et récupérer la réponse
      const response = await api.employes.update(editedEmployee.id, updateData);

      // Mettre à jour la liste avec les données reçues du serveur
      const updatedEmployees = employees.map(employee =>
        employee.id === editedEmployee.id ? response : employee
      );

      setEmployees(updatedEmployees);
      setSelectedEmployee(response);
      setIsEditing(false);
      setShowConfirmation(false);
      showSuccessToast('✨ Les modifications ont été enregistrées avec succès');

    } catch (error) {
      console.error('Erreur détaillée:', error);
      showErrorToast("Erreur lors de la mise à jour de l'employé");
    }
  };

  const handleCancelEdit = () => {
    // Réinitialiser tous les états liés à l'édition
    setIsEditing(false);
    setEditedEmployee(null);
    setShowConfirmation(false);
    showSuccessToast('Modifications annulées');
  };

  // Un employé qui part est radié, jamais supprimé d'ici : la suppression
  // effaçait sa paie, ses avances et ses caisses (18 septembre 2026).
  // Chaque ouverture remonte la fenêtre (clé) : elle part de zéro, roue de
  // chargement comprise, au lieu d'afficher un instant l'état précédent.
  const [cleRadiation, setCleRadiation] = useState(0);

  const handleDeleteClick = (employee) => {
    setEmployeeToDelete(employee);
    setCleRadiation((cle) => cle + 1);
    setIsDeleteModalOpen(true);
  };

  // L'employé est gardé pendant l'animation de fermeture : le vider ici
  // changeait le titre de la fenêtre pendant qu'elle disparaissait.
  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  const handleRadie = (resultat) => {
    // Sans validation, il quitte la liste tout de suite ; avec validation, il
    // y reste jusqu'à la décision du Centre de Support.
    if (resultat?.validation_required === false) {
      setEmployees(employees.filter(e => e.id !== employeeToDelete.id));
    }
    setIsDeleteModalOpen(false);
    // ⚠️ MÊME FORME QUE LA RADIATION D'UN ÉLÈVE, donc même traitement. Quand la
    // validation est requise, l'employé RESTE dans la liste — la ligne juste
    // au-dessus ne le retire que si `validation_required === false` — et ce
    // message est alors la seule trace du geste. Huit secondes pour le lire.
    // Sans validation, il quitte la liste sous les yeux : les 3 secondes suffisent.
    showSuccessToast(
      resultat?.message || 'Radiation enregistrée',
      resultat?.validation_required === false ? {} : { duration: 8000 }
    );
  };

  // Composant ConfirmationModal
  const ConfirmationModal = ({ isOpen, onConfirm, onCancel }) => (
    <Dialog.Root open={isOpen} onOpenChange={onCancel}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50" />
        <Dialog.Content className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white p-6 rounded-lg w-full max-w-md">
          <Dialog.Title className="text-xl font-bold mb-4">
            Confirmation de modification
          </Dialog.Title>
          <p>Voulez-vous confirmer cette modification ?</p>
          <div className="mt-6 flex justify-end space-x-2">
            <Button variant="outline" onClick={onCancel}>Non</Button>
            <Button onClick={onConfirm} className="bg-green-600 hover:bg-green-700 text-white">
              Oui
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );



  const handleViewDetails = async (employee) => {
    try {
      console.log(" Récupération des détails pour l'employé:", employee.id);
      const response = await api.employes.getOne(employee.id);
      console.log(" Réponse complète de l'API:", response);

      if (response.success) {
        console.log(" Données complètes reçues:", response.data);
        console.log(" Contact d'urgence:", response.data.contact_urgence);
        setSelectedEmployee(response.data);
        setIsDetailsOpen(true);
      } else {
        throw new Error(response.message || 'Erreur lors de la récupération des détails');
      }
    } catch (error) {
      console.error(" Erreur lors de la récupération des détails:", error);
      showErrorToast(`Erreur lors de la récupération des détails: ${error.message}`);
    }
  };

  // ── ARRIVÉE DEPUIS L'ONGLET « BADGES RFID » ────────────────────────────────
  // Cet écran ouvre la fiche demandée de lui-même quand on y arrive par le
  // bouton « Ouvrir la fiche » des paramètres de pointage. L'identifiant voyage
  // dans l'état de navigation, pas dans l'adresse : il ne vaut que le temps d'un
  // clic, et un `?employe=12` se mettrait en favori.
  //
  // ⚠️ L'ÉTAT EST CONSOMMÉ PUIS EFFACÉ (`replace: true`). Sans cela, il survit
  // au retour arrière et à tout remontage : le visiteur referme la fiche, revient
  // sur la liste, et elle se rouvre toute seule. C'est le défaut déjà payé sur
  // l'onglet Scolarité de l'application des parents, corrigé de la même façon.
  //
  // Rien ne se déclenche quand on arrive ici normalement : sans état, l'effet
  // sort à sa première ligne, et cet écran se comporte exactement comme avant.
  const employeDemande = location.state?.employeId;
  useEffect(() => {
    if (!employeDemande) return;
    handleViewDetails({ id: employeDemande });
    navigate(location.pathname, { replace: true, state: null });
    // `handleViewDetails` est recréé à chaque rendu : le mettre en dépendance
    // rouvrirait la fiche en boucle. Seul l'identifiant compte ici.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeDemande]);

  // ── ARRIVÉE PAR LE LIEN DIRECT « SANS PHOTO » (voir lireLienFiltres) ───────
  // Chaque navigation, reconnue à sa clé, est appliquée une fois : un second
  // clic sur le même lien la réapplique, même si l'agent a changé de filtres
  // entre-temps. Les paramètres ne quittent l'adresse qu'une fois la liste
  // chargée : si l'écran est démonté pendant le chargement (vérification des
  // permissions), le remontage reprend le lien.
  //
  // On relit l'adresse du moment (window.location.search) plutôt que celle du
  // rendu : seuls `photo` et `categorie` sont retirés, le reste est gardé.
  const lienFiltresApplique = useRef(null);
  useEffect(() => {
    const lien = lireLienFiltres(location.search);
    if (!lien) {
      lienFiltresApplique.current = null;
      return;
    }
    if (lienFiltresApplique.current !== location.key) {
      lienFiltresApplique.current = location.key;
      setVueRadies(false);
      resetListFilters();
      setFiltrePhoto(lien.photo);
      setFilterCategorie(lien.categorie);
    }
    if (loading) return;
    const actuels = new URLSearchParams(window.location.search);
    if (!actuels.has(PARAM_PHOTO) && !actuels.has(PARAM_CATEGORIE)) return;
    actuels.delete(PARAM_PHOTO);
    actuels.delete(PARAM_CATEGORIE);
    const reste = actuels.toString();
    navigate(
      { pathname: location.pathname, search: reste ? `?${reste}` : '' },
      { replace: true, state: location.state }
    );
    // `resetListFilters` et `navigate` changent à chaque rendu : les mettre en
    // dépendance relancerait l'effet à chaque frappe. Seules la navigation et
    // la fin du chargement comptent ici.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, location.search, loading]);

  const basculerFiltrePhoto = () => {
    setFiltrePhoto(prev => (prev === VALEUR_SANS_PHOTO ? '' : VALEUR_SANS_PHOTO));
    setCurrentPage(1);
  };


  const handleEditEmployee = async (employee) => {
    try {
      console.log(" Récupération des détails pour modification:", employee.id);
      const response = await api.employes.getOne(employee.id);

      if (response.success) {
        console.log(" Données complètes pour modification:", response.data);
        setSelectedEmployee(response.data);
        setIsEditing(true);
        setIsDetailsOpen(true);
      } else {
        throw new Error(response.message || 'Erreur lors de la récupération des détails');
      }
    } catch (error) {
      console.error(" Erreur lors de la récupération pour modification:", error);
      showErrorToast(`Erreur lors de la récupération des détails: ${error.message}`);
    }
  };




  const toastRendu = toastInfo && (
    shouldUseLongToast(toastInfo.message, toastInfo.type, toastInfo) ? (
      <LongToast
        show
        title={toastInfo.title}
        message={toastInfo.message}
        type={toastInfo.type}
        onClose={() => setToastInfoState(null)}
      />
    ) : (
      <Toast
        duration={toastInfo.duration}
        message={toastInfo.message}
        type={toastInfo.type}
        onClose={() => setToastInfoState(null)}
      />
    )
  );

  // Vue « Employés radiés » : remplace la liste, retour par le bouton. Une
  // restauration remet l'employé dans la liste : elle est rechargée au retour.
  if (vueRadies) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
        <button
          type="button"
          onClick={() => {
            setVueRadies(false);
            if (radiesModifies) {
              setRadiesModifies(false);
              refreshEmployeesList();
            }
          }}
          className="mb-4 inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-4 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
        >
          <ArrowLeft size={16} />
          Retour à la liste des employés
        </button>
        {/*
          Le troisième argument `options` passe jusqu'aux aiguilleurs : il était
          jeté ici et dans `signaler` (employes/EmployesRadies.jsx), alors que la
          plomberie de cet écran accepte déjà `long` et arbitre plus haut. Tout
          était prêt sauf ces deux passages d'arguments.
        */}
        <EmployesRadies
          onMessage={(message, type, options) => (type === 'error' ? showErrorToast(message, options) : showSuccessToast(message, options))}
          onChangement={() => setRadiesModifies(true)}
        />
        {toastRendu}
      </div>
    );
  }

  // Rendu principal
  return (


    <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">




      <div className="bg-white mb-8 p-6 rounded-2xl shadow-sm border border-gray-100">
        {/* La barre passe à la ligne sur un écran étroit au lieu de déborder :
            quatre boutons ne tiennent plus à côté du champ sur un téléphone. */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex-1 min-w-[240px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Rechercher un employé..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#246990] focus:border-[#246990] transition-all"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleExportExcel}
              className="px-4 py-2 bg-[#246990] text-white rounded-xl hover:bg-[#246990]/90 flex items-center gap-2"
            >
              <Download size={18} />
              Export Excel
            </button>

            {/* Le PDF est bâti par le serveur, qui ne connaît pas le filtre
                « Sans photo » : il rendrait tout le personnel. L'Excel, lui,
                suit la liste affichée. */}
            <button
              onClick={handleExportPDF}
              disabled={filtreSansPhotoActif}
              title={filtreSansPhotoActif ? "L'export PDF ne suit pas le filtre Sans photo : utilisez l'export Excel" : undefined}
              aria-describedby={filtreSansPhotoActif ? 'note-export-pdf-sans-photo' : undefined}
              className="px-4 py-2 bg-[#319190] text-white rounded-xl hover:bg-[#319190]/90 flex items-center gap-2 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-[#319190]"
            >
              <FileText size={18} />
              Export PDF
            </button>

            {/* Bascule « Sans photo » : gris neutre au repos (le manque n'est
                pas une alerte), teintée de bleu KLASYS quand elle filtre, comme
                celle de la liste des élèves : un aplat bleu se confondrait avec
                le bouton d'action Export Excel voisin. Pas d'icône. Le chiffre
                attend la fin du premier chargement : un « 0 » d'avant les
                données se lirait comme « tout le monde a sa photo ». */}
            <button
              type="button"
              onClick={basculerFiltrePhoto}
              aria-pressed={filtreSansPhotoActif}
              className={`px-4 py-2 rounded-xl border whitespace-nowrap transition-colors ${filtreSansPhotoActif
                ? 'bg-[#246990]/10 border-[#246990] text-[#246990] hover:bg-[#246990]/15'
                : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
            >
              {loading && employees.length === 0 ? 'Sans photo' : `Sans photo · ${employeesSansPhoto.length}`}
            </button>

            {peutVoirRadies && (
              <button
                onClick={() => setVueRadies(true)}
                className="px-4 py-2 bg-white border border-[#c66d59]/40 text-[#c66d59] rounded-xl hover:bg-[#c66d59]/5 flex items-center gap-2"
              >
                <UserX size={18} />
                Employés radiés
              </button>
            )}


            {/* <button 
              onClick={() => setShowFilters(prev => !prev)}
              className={`px-4 py-2 border rounded-xl flex items-center gap-2 transition-colors ${showFilters ? 'bg-[#246990] text-white border-[#246990]' : 'bg-white border-gray-200 hover:bg-gray-50 text-gray-700'}`}
            >
              <Filter size={18} />
              {showFilters ? 'Masquer filtres' : 'Filtres avancés'}
            </button>

            {hasActiveFilters && (
              <button
                onClick={resetListFilters}
                className="px-4 py-2 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 flex items-center gap-2 text-gray-700"
              >
                <RefreshCw size={18} />
                Réinitialiser
              </button>
            )} */}
          </div>
        </div>


        {/* Ce qui bloque une action s'écrit en clair dans la page
            (DESIGN-SYSTEM.md, Infobulles) : le title du bouton Export PDF
            ne s'affiche ni au toucher, ni tout de suite à la souris. */}
        {filtreSansPhotoActif && (
          <p id="note-export-pdf-sans-photo" className="mt-3 text-right text-xs text-gray-500">
            L'export PDF ne suit pas le filtre Sans photo : utilisez l'export Excel.
          </p>
        )}

        {/* Panneau des filtres */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Rôle</label>
                <select
                  value={filters.role}
                  onChange={(e) => updateFilters({ role: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#246990]"
                >
                  <option value="">Tous les rôles</option>
                  {roles.filter((role) => role.sous_licence !== false || Number(role.nombre_employes) > 0).map(role => (
                    <option key={role.id} value={role.nom}>{role.nom}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Statut</label>
                <select
                  value={filters.statut}
                  onChange={(e) => updateFilters({ statut: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#246990]"
                >
                  <option value="">Tous les statuts</option>
                  <option value="actif">Actif</option>
                  <option value="En congé">En congé</option>
                  <option value="Suspendu">Suspendu</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date d'entrée - du</label>
                <input
                  type="date"
                  value={filters.dateDebut}
                  max={filters.dateFin || undefined}
                  onChange={(e) => updateFilters({ dateDebut: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#246990] focus:border-[#246990]"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date d'entrée - au</label>
                <input
                  type="date"
                  value={filters.dateFin}
                  min={filters.dateDebut || undefined}
                  onChange={(e) => updateFilters({ dateFin: e.target.value })}
                  className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#246990] focus:border-[#246990]"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <button
                type="button"
                onClick={() => setRecentDateFilter(7)}
                className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 text-gray-700"
              >
                7 derniers jours
              </button>
              <button
                type="button"
                onClick={() => setRecentDateFilter(30)}
                className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 text-gray-700"
              >
                30 derniers jours
              </button>
              <button
                type="button"
                onClick={setCurrentYearDateFilter}
                className="px-3 py-1.5 text-sm bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 text-gray-700"
              >
                Année scolaire active
              </button>
            </div>
          </div>
        )}

      </div>

      {/* Statistiques - Conteneur indépendant */}
      <StatistiquesRoles
        title="Répartition par catégorie RH"
        statsRoles={statsCategories}
        filterRole={filterCategorie}
        setFilterRole={setFilterCategorie}
      />

      <StatistiquesRoles
        title="Répartition par poste métier"
        statsRoles={statsRoles}
        filterRole={filterRole}
        setFilterRole={setFilterRole}
        collapsible
        defaultCollapsed
      />
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">

          <div className="text-base font-semibold text-gray-600 mt-2">
            Total : {sortedEmployees.length} employés • Affichés : {paginatedEmployees.length} sur {sortedEmployees.length}
          </div>

        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>


                <th
                  onClick={() => handleSort('nom')}
                  className="p-4 text-left text-gray-600 font-medium cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Nom
                    {sortConfig.key === 'nom' && (
                      <span>{sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('categorie_rh_libelle')}
                  className="p-4 text-left text-gray-600 font-medium cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Catégorie RH
                    {sortConfig.key === 'categorie_rh_libelle' && (
                      <span>{sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('poste_metier_nom')}
                  className="p-4 text-left text-gray-600 font-medium cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Poste métier
                    {sortConfig.key === 'poste_metier_nom' && (
                      <span>{sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}</span>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('groupe_nom')}
                  className="p-4 text-left text-gray-600 font-medium cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Groupe sécurité
                    {sortConfig.key === 'groupe_nom' && (
                      <span>{sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}</span>
                    )}
                  </div>
                </th>
                <th className="p-4 text-left text-gray-600 font-medium">{'Profil rémunération'}</th>
                <th className="p-4 text-left text-gray-600 font-medium">Contact</th>
                <th
                  onClick={() => handleSort('date_embauche')}
                  className="p-4 text-left text-gray-600 font-medium cursor-pointer hover:bg-gray-100"
                >
                  <div className="flex items-center gap-2">
                    Date d'entrée
                    {sortConfig.key === 'date_embauche' && (
                      <span>{sortConfig.direction === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}</span>
                    )}
                  </div>
                </th>
                <th className="p-4 text-left text-gray-600 font-medium">Statut</th>
                <th className="p-4 text-center text-gray-600 font-medium">Actions</th>
              </tr>
            </thead>

            <tbody>
              {filtreSansPhotoActif && !loading && employees.length > 0 && sortedEmployees.length === 0 && (
                <tr>
                  <td colSpan={9} className="p-8 text-center text-sm text-gray-500">
                    {autresFiltresActifs
                      ? 'Personne n\'est sans photo parmi le personnel filtré.'
                      : 'Tout le personnel a sa photo.'}
                  </td>
                </tr>
              )}
              {paginatedEmployees.map((employee) => (
                <tr key={employee.id} className="border-b border-gray-100 hover:bg-gray-50/50 transition-colors">

                  <td className="p-4">
                    <div className="flex items-center">

                      <div className="w-8 h-8 rounded-full bg-[#246990] text-white flex items-center justify-center mr-3">
                        <User size={16} />
                      </div>

                      <div>
                        <div className="font-medium text-gray-800">{employee.nom} {employee.prenom}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 align-middle">
                    <span
                      className="inline-flex h-[34px] w-[136px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#246990]/10 px-3 text-center text-[12px] font-medium leading-[14px] text-[#246990]"
                      title={getCategorieRh(employee)}
                    >
                      <span className="max-w-full whitespace-normal break-words">
                        {getCategorieRh(employee)}
                      </span>
                    </span>
                  </td>
                  <td className="p-4 align-middle">
                    {shouldUseCompactPostes(employee) ? (
                      <div className="flex max-w-[150px] flex-col gap-1.5">
                        {getPostesMetiersDetails(employee).slice(0, 3).map(renderCompactPosteBadge)}
                        {getPostesMetiersDetails(employee).length > 3 && (
                          <span className="inline-flex h-[24px] w-[136px] items-center justify-center rounded-full border border-gray-200 bg-gray-100 px-2 text-[11px] font-medium text-gray-600">
                            +{getPostesMetiersDetails(employee).length - 3}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="font-medium text-gray-800">{getPosteMetier(employee)}</div>
                    )}
                  </td>
                  <td className="p-4">
                    <div className="space-y-1">
                      <div>{getGroupeSecurite(employee)}</div>
                      {(employee.nombre_groupes_securite || 0) > 1 && (
                        <div className="text-xs text-gray-500">{getAllGroupesSecurite(employee)}</div>
                      )}
                    </div>
                  </td>

                  <td className="p-4">
                    <span className={`inline-flex px-2 py-1 rounded-full text-sm border ${getProfilBadgeClass(employee)}`}>
                      {getProfilRemunerationLabel(employee)}
                    </span>
                  </td>

                  <td className="p-4">
                    <div className="text-sm">
                      <div>{employee.email}</div>
                      <div className="text-gray-500">{employee.telephone}</div>
                    </div>
                  </td>
                  <td className="p-4">{employee.date_embauche ? formatDate(employee.date_embauche) : ""}</td>
                  <td className="p-4">


                    <span
                      className={`px-2 py-1 rounded-full text-sm ${(employee.statut === 'actif' || employee.statut === 'Actif' || !employee.statut)
                          ? 'bg-[#319190]/15 text-[#319190]'      // Actif
                          : employee.statut === 'En congé'
                            ? 'bg-yellow-100 text-yellow-800'      // En congé
                            : 'bg-[#c66d59]/15 text-[#c66d59]'     // Inactif / autre
                        }`}
                    >
                      {employee.statut || 'Actif'}
                    </span>



                  </td>
                  <td className="p-4">
                    <div className="flex justify-center gap-2">

                      <button
                        onClick={() => handleViewDetails(employee)}
                        className="p-2 bg-[#3084b5] hover:bg-[#3084b5]/90 rounded-lg transition-colors"
                        title="Dossier"
                      >
                        <FileText size={16} className="text-white" />
                      </button>


                      {peutRadier && (
                        <button
                          onClick={() => handleDeleteClick(employee)}
                          className="p-2 bg-[#c66d59] hover:bg-[#c66d59]/90 rounded-lg transition-colors"
                          title="Radier"
                        >
                          <UserMinus size={16} className="text-white" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Pagination KLASYS (ui/Pagination) : même barre que les autres listes. */}
          <div className="flex flex-wrap items-center gap-3 border-t border-gray-100 px-4 py-3">
            <ChampSelect
              valeur={itemsPerPage}
              onChange={(valeur) => {
                setItemsPerPage(Number(valeur));
                setCurrentPage(1);
              }}
              options={[10, 25, 50].map((n) => ({ valeur: n, libelle: `${n} par page` }))}
              className="w-[140px]"
            />
            <div className="min-w-0 flex-1">
              <Pagination
                page={pageCourante}
                nombrePages={totalPages}
                total={sortedEmployees.length}
                parPage={itemsPerPage}
                onChangerPage={setCurrentPage}
                libelleElement="employé"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Modaux */}
      {selectedEmployee && (
        <DetailModal
          employee={selectedEmployee}
          isOpen={isDetailsOpen}
          onClose={() => {
            setIsDetailsOpen(false);
            setIsEditing(false);
            setEditedEmployee(null);
          }}
          isEditing={isEditing}
          onUpdate={handleEditSubmit}
          // La photo a sa propre route, déjà appelée par la fiche : ici on ne
          // fait que l'afficher, sans renvoyer la fiche entière au serveur.
          onPhotoChange={(employeId, photoUrl) => {
            setEmployees(prev => prev.map(e => (e.id === employeId ? { ...e, photo_url: photoUrl } : e)));
            setSelectedEmployee(prev => (prev && prev.id === employeId ? { ...prev, photo_url: photoUrl } : prev));
          }}
          onShowToast={(toastData) => setToastInfo(toastData)}
          roles={roles}
          departements={departements}
        />
      )}

      <ConfirmationModal
        isOpen={showConfirmation}
        onConfirm={handleConfirmEdit}
        onCancel={handleCancelEdit}
      />

      <RadiationEmployeDialog
        key={cleRadiation}
        employe={employeeToDelete}
        open={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onRadie={handleRadie}
      />

      {toastRendu}
    </div>

  );
};

export default EmployeesList;